/* Concept collection page behaviour (sections/ac-col-main.liquid).
   Spec: docs/superpowers/specs/2026-10-02-collection-concept-design.md

   - Facets, grade chips, sort, price and page links swap [data-ac-col-swap]
     from a ?section_id= response (View Transitions where supported) and
     push history; Back/Forward restore. Without JS they are plain links
     and GET forms.
   - Load more appends the next page's cards.
   - Size lineup: radios that set the variant, price, $/unit and the card
     photo; the dashed price curve is drawn from the dots' rendered positions.
   - Add to cart posts to /cart/add.js, then refreshes the header count and
     total the way assets/quick-order.js does. Without JS the form posts.
   - Filter drawer below 1024px; grid/table preference per browser.
   - Hero specimens: thumbnails + arrows; the WebGL turntable
     (assets/ac-col-gl.js) on capable desktops. */
(function () {
  "use strict";

  var root = document.querySelector("[data-ac-col]");
  if (!root || root.__acCol) return;
  root.__acCol = true;

  var sectionId = root.getAttribute("data-section-id");
  var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  var desktop = window.matchMedia("(min-width: 1024px)");
  var params = new URLSearchParams(location.search);
  var view = params.get("view");
  var previewTheme = params.get("preview_theme_id");
  var THREE_CDN_URL =
    "https://cdn.jsdelivr.net/npm/three@0.160.0/build/three.module.min.js";

  function debug() {
    if (window.AC_COL_DEBUG && window.console)
      console.debug.apply(
        console,
        ["ac-col"].concat(Array.prototype.slice.call(arguments)),
      );
  }

  /* The alternate template is reached with ?view=ac-concept while it is a
     preview; keep it on links and fetches so the preview doesn't fall back
     to the default template. Once a collection is assigned the template
     there is no view param and nothing is added. */
  function withView(href) {
    var u = new URL(href, location.href);
    if (view && !u.searchParams.has("view")) u.searchParams.set("view", view);
    if (previewTheme && !u.searchParams.has("preview_theme_id"))
      u.searchParams.set("preview_theme_id", previewTheme);
    return u;
  }
  function keepView(scope) {
    if (!view) return;
    scope.querySelectorAll("a[data-ac-col-keep-view]").forEach(function (a) {
      a.href = withView(a.href).toString();
    });
  }
  var hero = document.querySelector("[data-ac-col-hero]");
  keepView(root);
  if (hero) keepView(hero);

  /* ------------------------------------------------------------ */
  /* Sticky offsets                                               */
  /* ------------------------------------------------------------ */

  function measureToolbar() {
    var bar = root.querySelector("[data-ac-col-toolbar]");
    if (bar)
      root.style.setProperty("--ac-col-toolbar-h", bar.offsetHeight + "px");
  }

  /* ------------------------------------------------------------ */
  /* View: grid / table                                           */
  /* ------------------------------------------------------------ */

  var VIEW_KEY = "ac-col-view";
  var layout = "grid";
  try {
    layout = localStorage.getItem(VIEW_KEY) === "table" ? "table" : "grid";
  } catch (e) {
    /* storage blocked: default grid */
  }
  function applyLayout() {
    root.classList.toggle("is-table", layout === "table");
    root.querySelectorAll("[data-ac-col-view]").forEach(function (b) {
      b.setAttribute(
        "aria-pressed",
        String(b.getAttribute("data-ac-col-view") === layout),
      );
    });
    root.querySelectorAll("[data-ac-col-lineup]").forEach(drawCurve);
  }

  /* ------------------------------------------------------------ */
  /* Size lineup                                                  */
  /* ------------------------------------------------------------ */

  function drawCurve(lineup) {
    var line = lineup.querySelector("[data-ac-col-curve]");
    if (!line) return;
    var row = lineup.querySelector(".ac-col-lineup__row");
    var svg = line.ownerSVGElement;
    var rb = row.getBoundingClientRect();
    var sb = svg.getBoundingClientRect();
    if (!rb.width || !sb.height) return;
    var pts = [];
    lineup.querySelectorAll(".ac-col-lineup__dot").forEach(function (d) {
      var b = d.getBoundingClientRect();
      var x = ((b.left + b.width / 2 - sb.left) / sb.width) * 100;
      var y = ((b.top + b.height / 2 - sb.top) / sb.height) * 100;
      pts.push(x.toFixed(2) + "," + y.toFixed(2));
    });
    line.setAttribute("points", pts.join(" "));
  }

  /* The card photo follows the size. Each radio carries its variant's photo
     (data-photo); a size without one falls back to the product's own photo.
     Widths match the image_tag in snippets/ac-col-card.liquid. */
  var PHOTO_WIDTHS = [240, 360, 480, 640];

  function photoSources(url) {
    var u = new URL(url, location.href);
    var set = PHOTO_WIDTHS.map(function (w) {
      u.searchParams.set("width", w);
      return u.href + " " + w + "w";
    });
    return { src: u.href, srcset: set.join(", ") };
  }

  function cardPhoto(el) {
    var card = el.closest("[data-ac-col-card]");
    return card && card.querySelector(".ac-col-card__photo");
  }

  function swapPhoto(btn) {
    var img = cardPhoto(btn);
    if (!img) return;
    if (!img.__acOwn) {
      img.__acOwn = {
        src: img.getAttribute("src"),
        srcset: img.getAttribute("srcset") || "",
      };
    }
    var url = btn.getAttribute("data-photo");
    var next = url ? photoSources(url) : img.__acOwn;
    img.__acWant = next.srcset;
    if (img.getAttribute("srcset") === next.srcset) return;
    // Load off-DOM first so the well never shows an empty frame.
    var pre = new Image();
    pre.onload = function () {
      if (img.__acWant !== next.srcset) return;
      img.srcset = next.srcset;
      img.src = next.src;
      if (!reduceMotion.matches && img.animate) {
        img.animate([{ opacity: 0.35 }, { opacity: 1 }], {
          duration: 180,
          easing: "ease-out",
        });
      }
    };
    pre.sizes = img.sizes;
    pre.srcset = next.srcset;
    pre.src = next.src;
  }

  // Fetch a card's other size photos the first time its lineup gets
  // attention, so stepping through the sizes swaps without a wait.
  function warmPhotos(lineup) {
    if (lineup.__acWarm) return;
    lineup.__acWarm = true;
    var img = cardPhoto(lineup);
    if (!img) return;
    lineup.querySelectorAll("[data-photo]").forEach(function (b) {
      var next = photoSources(b.getAttribute("data-photo"));
      var pre = new Image();
      pre.sizes = img.sizes;
      pre.srcset = next.srcset;
      pre.src = next.src;
    });
  }

  function selectSize(btn, focus) {
    var lineup = btn.closest("[data-ac-col-lineup]");
    var form = btn.closest("[data-ac-col-buy]");
    if (!lineup || !form) return;
    lineup.querySelectorAll('[role="radio"]').forEach(function (b) {
      var on = b === btn;
      b.setAttribute("aria-checked", String(on));
      b.tabIndex = on ? 0 : -1;
    });
    if (focus) btn.focus();
    var id = form.querySelector("[data-ac-col-variant]");
    if (id) id.value = btn.getAttribute("data-variant");
    var size = form.querySelector("[data-ac-col-size]");
    var price = form.querySelector("[data-ac-col-price]");
    var unit = form.querySelector("[data-ac-col-unit]");
    if (size) size.textContent = btn.getAttribute("data-label") || "";
    if (price) price.textContent = btn.getAttribute("data-price") || "";
    if (unit) unit.textContent = btn.getAttribute("data-unit") || "";
    var add = form.querySelector("[data-ac-col-add]");
    var label = form.querySelector("[data-ac-col-add-label]");
    var available = btn.getAttribute("data-available") === "true";
    if (add) {
      add.disabled = !available;
      add.classList.remove("is-added");
    }
    if (label) label.textContent = available ? "Add to cart" : "Sold out";
    var msg = form.querySelector("[data-ac-col-msg]");
    if (msg) msg.hidden = true;
    swapPhoto(btn);
  }

  function initCards(scope) {
    scope.querySelectorAll("[data-ac-col-lineup]").forEach(function (lineup) {
      if (lineup.__acInit) return;
      lineup.__acInit = true;
      drawCurve(lineup);
    });
  }

  root.addEventListener("click", function (e) {
    var size = e.target.closest(".ac-col-lineup__size");
    if (size) {
      selectSize(size, false);
      return;
    }
  });
  ["pointerover", "focusin"].forEach(function (type) {
    root.addEventListener(type, function (e) {
      var lineup = e.target.closest && e.target.closest("[data-ac-col-lineup]");
      if (lineup) warmPhotos(lineup);
    });
  });
  root.addEventListener("keydown", function (e) {
    var size = e.target.closest && e.target.closest(".ac-col-lineup__size");
    if (!size) return;
    var keys = {
      ArrowRight: 1,
      ArrowDown: 1,
      ArrowLeft: -1,
      ArrowUp: -1,
      Home: "first",
      End: "last",
    };
    if (!(e.key in keys)) return;
    e.preventDefault();
    var all = Array.prototype.slice.call(
      size.parentNode.querySelectorAll('[role="radio"]'),
    );
    var i = all.indexOf(size);
    var k = keys[e.key];
    var next =
      k === "first"
        ? all[0]
        : k === "last"
          ? all[all.length - 1]
          : all[(i + k + all.length) % all.length];
    selectSize(next, true);
  });

  /* ------------------------------------------------------------ */
  /* Add to cart                                                  */
  /* ------------------------------------------------------------ */

  function money(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === "function")
      return Shopify.formatMoney(cents, "${{amount}}");
    return "$" + (cents / 100).toFixed(2);
  }

  function refreshHeader() {
    return fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        document
          .querySelectorAll("[data-header-cart-total]")
          .forEach(function (el) {
            el.textContent = money(cart.total_price);
            el.classList.remove("hidden");
          });
        document
          .querySelectorAll(
            "[data-header-cart-count], .cart-count-bubble span, [data-cart-count]",
          )
          .forEach(function (el) {
            el.textContent = cart.item_count;
            el.classList.remove("hidden");
          });
        document.dispatchEvent(
          new CustomEvent("ac:cart-updated", { detail: { cart: cart } }),
        );
      });
  }

  root.addEventListener("submit", function (e) {
    var form = e.target.closest("[data-ac-col-buy]");
    if (!form) return;
    e.preventDefault();
    var btn = form.querySelector("[data-ac-col-add]");
    var label = form.querySelector("[data-ac-col-add-label]");
    var msg = form.querySelector("[data-ac-col-msg]");
    if (!btn || btn.disabled || btn.getAttribute("aria-busy") === "true")
      return;
    var id = parseInt(form.querySelector("[data-ac-col-variant]").value, 10);
    btn.setAttribute("aria-busy", "true");
    label.textContent = "Adding…";
    msg.hidden = true;
    msg.classList.remove("is-error");
    fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ items: [{ id: id, quantity: 1 }] }),
    })
      .then(function (r) {
        return r
          .json()
          .catch(function () {
            return {};
          })
          .then(function (body) {
            if (!r.ok)
              throw new Error(
                body.description ||
                  body.message ||
                  "Couldn't add that to the cart. Please try again.",
              );
            return body;
          });
      })
      .catch(function (err) {
        if (err instanceof TypeError)
          throw new Error("Network problem. Please try again.");
        throw err;
      })
      .then(function () {
        btn.classList.add("is-added");
        label.textContent = "Added";
        msg.innerHTML = 'In your cart. <a href="/cart">View cart</a>';
        msg.hidden = false;
        return refreshHeader().catch(function (err) {
          debug("header refresh failed", err);
        });
      })
      .catch(function (err) {
        label.textContent = "Add to cart";
        msg.textContent = err.message;
        msg.classList.add("is-error");
        msg.hidden = false;
      })
      .then(function () {
        btn.removeAttribute("aria-busy");
        setTimeout(function () {
          if (btn.classList.contains("is-added")) {
            btn.classList.remove("is-added");
            label.textContent = "Add another";
          }
        }, 2600);
      });
  });

  /* ------------------------------------------------------------ */
  /* Filter drawer (< 1024px)                                     */
  /* ------------------------------------------------------------ */

  var lastOpener = null;
  var prevOverflow = null;
  function rail() {
    return root.querySelector("[data-ac-col-rail]");
  }
  function openRail(opener) {
    var r = rail();
    if (!r || desktop.matches) return;
    lastOpener = opener || null;
    r.classList.add("is-open");
    r.setAttribute("role", "dialog");
    r.setAttribute("aria-modal", "true");
    var scrim = root.querySelector(".ac-col-scrim");
    if (scrim) scrim.hidden = false;
    if (prevOverflow === null)
      prevOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    root
      .querySelectorAll("[data-ac-col-rail-open]")
      .forEach(function (b) {
        b.setAttribute("aria-expanded", "true");
      });
    r.focus({ preventScroll: true });
    if (fab) fab.hidden = true;
  }
  function closeRail(restore) {
    var r = rail();
    if (!r) return;
    r.classList.remove("is-open");
    r.removeAttribute("role");
    r.removeAttribute("aria-modal");
    var scrim = root.querySelector(".ac-col-scrim");
    if (scrim) scrim.hidden = true;
    if (prevOverflow !== null) {
      document.documentElement.style.overflow = prevOverflow;
      prevOverflow = null;
    }
    root
      .querySelectorAll("[data-ac-col-rail-open]")
      .forEach(function (b) {
        b.setAttribute("aria-expanded", "false");
      });
    if (restore !== false) {
      var back =
        (lastOpener && document.contains(lastOpener) && lastOpener) ||
        root.querySelector("[data-ac-col-rail-open]");
      if (back) back.focus({ preventScroll: true });
    }
  }
  function railOpen() {
    var r = rail();
    return !!(r && r.classList.contains("is-open"));
  }
  root.addEventListener("click", function (e) {
    var opener = e.target.closest("[data-ac-col-rail-open]");
    if (opener) {
      openRail(opener);
      return;
    }
    if (e.target.closest("[data-ac-col-rail-close]")) closeRail();
  });
  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape" && railOpen()) closeRail();
    if (e.key === "Tab" && railOpen()) {
      var r = rail();
      var f = r.querySelectorAll(
        'a[href], button:not([disabled]), input, select, summary, [tabindex="0"]',
      );
      if (!f.length) return;
      var first = f[0];
      var last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });
  desktop.addEventListener("change", function () {
    if (desktop.matches && railOpen()) closeRail(false);
  });

  /* ------------------------------------------------------------ */
  /* Results: swap, history, load more                            */
  /* ------------------------------------------------------------ */

  /* Separate requests for "replace the results" and "append a page", so
     one never silently cancels the other. A navigation cancels a pending
     Load more (its page would belong to the old results); Load more waits
     while a navigation is in flight. */
  var navCtl = null;
  var moreCtl = null;
  var lastSearch = location.search;

  function sectionUrl(href) {
    var u = withView(href);
    u.searchParams.set("section_id", sectionId);
    return u.toString();
  }

  function fetchSection(href, ctl) {
    return fetch(sectionUrl(href), {
      signal: ctl.signal,
      headers: { Accept: "text/html" },
    })
      .then(function (r) {
        if (!r.ok) throw new Error("HTTP " + r.status);
        return r.text();
      })
      .then(function (html) {
        return new DOMParser().parseFromString(html, "text/html");
      });
  }

  function shell() {
    return root.querySelector("[data-ac-col-swap]");
  }

  function cssEscape(v) {
    if (window.CSS && CSS.escape) return CSS.escape(v);
    return String(v).replace(/["\\]/g, "\\$&");
  }

  function announce(text) {
    var live = root.querySelector("[data-ac-col-announce]");
    if (!live) return;
    live.textContent = "";
    setTimeout(function () {
      live.textContent = text;
    }, 60);
  }

  /* After a filter, sort or page change, bring the top of the results
     back into view if the shopper had scrolled past it. The shell's own top
     is where the toolbar sits before it sticks. */
  function scrollToResults() {
    var sh = shell();
    var bar = root.querySelector("[data-ac-col-toolbar]");
    if (!sh || !bar) return;
    var stick =
      parseFloat(getComputedStyle(bar).top) ||
      parseFloat(
        getComputedStyle(document.body).getPropertyValue("--headerHeight"),
      ) ||
      0;
    var top = sh.getBoundingClientRect().top;
    if (top < stick - 1) {
      window.scrollTo({
        top: window.scrollY + top - stick,
        behavior: reduceMotion.matches ? "auto" : "smooth",
      });
    }
  }

  function navigate(href, push, focusKey) {
    var current = shell();
    if (!current) {
      location.href = href;
      return;
    }
    if (navCtl) navCtl.abort();
    if (moreCtl) moreCtl.abort();
    var ctl = (navCtl = new AbortController());
    var wasOpen = railOpen();
    var active = document.activeElement;
    var hadFocus = !!(active && current.contains(active));
    if (!focusKey && hadFocus && active.getAttribute)
      focusKey = active.getAttribute("data-ac-col-key");
    var openFacets = {};
    current.querySelectorAll("[data-ac-col-facet]").forEach(function (d) {
      openFacets[d.getAttribute("data-ac-col-facet")] = d.open;
    });
    var railScroll = rail() ? rail().scrollTop : 0;
    current.classList.add("is-loading");
    current.setAttribute("aria-busy", "true");

    fetchSection(href, ctl)
      .then(function (doc) {
        if (ctl !== navCtl) return;
        navCtl = null;
        var next = doc.querySelector("[data-ac-col-swap]");
        if (!next) throw new Error("section response had no results region");
        next.querySelectorAll("[data-ac-col-facet]").forEach(function (d) {
          var k = d.getAttribute("data-ac-col-facet");
          if (k in openFacets) d.open = openFacets[k];
        });
        if (wasOpen) {
          /* open before insertion, so the drawer doesn't slide in again */
          var nr = next.querySelector("[data-ac-col-rail]");
          if (nr) {
            nr.classList.add("is-open");
            nr.setAttribute("role", "dialog");
            nr.setAttribute("aria-modal", "true");
          }
          var ns = next.querySelector(".ac-col-scrim");
          if (ns) ns.hidden = false;
          next
            .querySelectorAll("[data-ac-col-rail-open]")
            .forEach(function (b) {
              b.setAttribute("aria-expanded", "true");
            });
        }
        function swap() {
          var live = shell();
          if (!live) return;
          live.replaceWith(next);
          after(next);
          trackList(next.querySelectorAll("[data-ac-col-card]"));
          if (!wasOpen) scrollToResults();
          if (wasOpen) {
            var r = rail();
            r.scrollTop = railScroll;
            var target =
              (focusKey &&
                r.querySelector(
                  '[data-ac-col-key="' + cssEscape(focusKey) + '"]',
                )) ||
              r;
            target.focus({ preventScroll: true });
          } else if (hadFocus) {
            /* keyboard users keep their place: the same control if it's
               still there, else the result count */
            var same =
              focusKey &&
              next.querySelector(
                '[data-ac-col-key="' + cssEscape(focusKey) + '"]',
              );
            var landing = same || next.querySelector("[data-ac-col-count]");
            if (landing) landing.focus({ preventScroll: true });
          }
        }
        if (document.startViewTransition && !reduceMotion.matches) {
          document.startViewTransition(swap);
        } else {
          swap();
        }
        var shown = withView(href);
        shown.searchParams.delete("section_id");
        if (push) history.pushState({ acCol: true }, "", shown.toString());
        lastSearch = location.search;
        store(null);
        var count = next.querySelector("[data-ac-col-count]");
        if (count) announce(count.textContent.replace(/\s+/g, " ").trim());
      })
      .catch(function (err) {
        if (err.name === "AbortError") return;
        debug("swap failed, loading the page", err);
        location.href = withView(href).toString();
      });
  }

  function after(scope) {
    keepView(scope);
    initCards(scope);
    applyLayout();
    measureToolbar();
    watchToolbar();
  }

  root.addEventListener("click", function (e) {
    var a = e.target.closest("a[data-ac-col-nav]");
    if (!a || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    if (a.classList.contains("ac-col-check")) a.classList.add("is-pending");
    navigate(a.href, true, a.getAttribute("data-ac-col-key"));
  });

  root.addEventListener("change", function (e) {
    var sel = e.target.closest(".ac-col-sort__select");
    if (!sel) return;
    var form = sel.form;
    var u = new URL(form.action, location.href);
    new FormData(form).forEach(function (v, k) {
      u.searchParams.append(k, v);
    });
    navigate(u.toString(), true);
  });

  root.addEventListener("submit", function (e) {
    var form = e.target.closest("[data-ac-col-price], [data-ac-col-sort]");
    if (!form) return;
    e.preventDefault();
    if (form.matches("[data-ac-col-price]")) {
      var nums = form.querySelectorAll('input[type="number"]');
      if (nums.length === 2 && nums[0].value !== "" && nums[1].value !== "") {
        var lo = parseFloat(nums[0].value);
        var hi = parseFloat(nums[1].value);
        if (lo > hi) {
          nums[0].value = String(hi);
          nums[1].value = String(lo);
        }
      }
    }
    var u = new URL(form.action, location.href);
    new FormData(form).forEach(function (v, k) {
      if (v !== "") u.searchParams.append(k, v);
    });
    navigate(u.toString(), true);
  });

  window.addEventListener("popstate", function () {
    /* fragment links (the skip link) fire popstate too: only refetch when
       the query actually changed */
    if (location.search === lastSearch) return;
    lastSearch = location.search;
    navigate(location.href, false);
  });

  root.addEventListener("click", function (e) {
    var more = e.target.closest("a[data-ac-col-more]");
    if (!more || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0)
      return;
    e.preventDefault();
    if (more.classList.contains("is-loading") || navCtl) return;
    var hadFocus = document.activeElement === more;
    var label = more.textContent;
    more.classList.add("is-loading");
    more.textContent = "Loading…";
    var ctl = (moreCtl = new AbortController());
    fetchSection(more.href, ctl)
      .then(function (doc) {
        if (ctl !== moreCtl) return;
        moreCtl = null;
        var grid = root.querySelector("[data-ac-col-grid]");
        var cards = doc.querySelectorAll("[data-ac-col-grid] > [data-ac-col-card]");
        var first = null;
        cards.forEach(function (c, i) {
          c.classList.add("is-new");
          c.style.setProperty("--i", String(i));
          grid.appendChild(c);
          if (!first) first = c;
        });
        var pager = root.querySelector("[data-ac-col-pager]");
        var nextPager = doc.querySelector("[data-ac-col-pager]");
        if (pager && nextPager) {
          /* keep "Showing 1–N": the cards above are still on the page */
          var from = pager.querySelector("[data-ac-col-from]");
          var keepFrom = from ? from.textContent : null;
          pager.replaceWith(nextPager);
          var nf = nextPager.querySelector("[data-ac-col-from]");
          if (nf && keepFrom) nf.textContent = keepFrom;
        }
        initCards(grid);
        keepView(grid);
        announce(cards.length + " more products loaded");
        rememberPages(more.href);
        trackList(cards);
        if (hadFocus && first) {
          var link = first.querySelector(".ac-col-card__title a");
          if (link) link.focus({ preventScroll: true });
        }
      })
      .catch(function (err) {
        if (err.name === "AbortError") {
          more.classList.remove("is-loading");
          more.textContent = label;
          return;
        }
        location.href = withView(more.href).toString();
      });
  });

  root.addEventListener("click", function (e) {
    var b = e.target.closest("[data-ac-col-view]");
    if (!b) return;
    layout = b.getAttribute("data-ac-col-view");
    try {
      localStorage.setItem(VIEW_KEY, layout);
    } catch (err) {
      /* not remembered; fine */
    }
    applyLayout();
  });

  /* Redraw curves when cards change width. */
  if ("ResizeObserver" in window) {
    var raf = 0;
    new ResizeObserver(function () {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(function () {
        root.querySelectorAll("[data-ac-col-lineup]").forEach(drawCurve);
        measureToolbar();
      });
    }).observe(root);
  }

  /* ------------------------------------------------------------ */
  /* Hero specimens                                               */
  /* ------------------------------------------------------------ */

  var spec = hero ? hero.querySelector("[data-ac-col-specimens]") : null;
  var thumbs = spec
    ? Array.prototype.slice.call(spec.querySelectorAll("[data-ac-col-spec]"))
    : [];
  var specIndex = 0;
  var scene = null;

  function showSpecimen(i, fromScene) {
    if (!thumbs.length) return;
    i = ((i % thumbs.length) + thumbs.length) % thumbs.length;
    specIndex = i;
    var t = thumbs[i];
    thumbs.forEach(function (b, j) {
      b.setAttribute("aria-pressed", String(j === i));
    });
    var link = spec.querySelector("[data-ac-col-cap-link]");
    var formula = spec.querySelector("[data-ac-col-cap-formula]");
    var cas = spec.querySelector("[data-ac-col-cap-cas]");
    link.textContent = t.getAttribute("data-name");
    link.href = t.getAttribute("data-url");
    link.title = t.getAttribute("data-title");
    var f = t.querySelector(".ac-col-spec__formula");
    formula.innerHTML = f ? f.innerHTML : "";
    cas.textContent = "CAS " + t.getAttribute("data-cas");
    var poster = spec.querySelector("[data-ac-col-poster]");
    var src = t.getAttribute("data-poster");
    if (poster && poster.getAttribute("src") !== src) {
      if (reduceMotion.matches || spec.classList.contains("is-live")) {
        poster.src = src;
      } else {
        poster.classList.add("is-swapping");
        var img = new Image();
        img.onload = img.onerror = function () {
          poster.src = src;
          requestAnimationFrame(function () {
            poster.classList.remove("is-swapping");
          });
        };
        img.src = src;
      }
    }
    if (scene && !fromScene) scene.focus(i);
  }

  if (spec && thumbs.length) {
    showSpecimen(0, true);
    spec.addEventListener("click", function (e) {
      var t = e.target.closest("[data-ac-col-spec]");
      if (t) {
        showSpecimen(thumbs.indexOf(t));
        return;
      }
      var s = e.target.closest("[data-ac-col-step]");
      if (s)
        showSpecimen(specIndex + parseInt(s.getAttribute("data-ac-col-step"), 10));
    });
    bootGL();
  }

  function loadModule(url) {
    /* Shopify's CDN minifier turns a literal import() into require(). */
    return Promise.resolve().then(function () {
      return new Function("u", "return import(u)")(url);
    });
  }
  var scripts = {};
  function loadScript(src) {
    if (!scripts[src]) {
      scripts[src] = new Promise(function (resolve, reject) {
        var s = document.createElement("script");
        s.src = src;
        s.async = true;
        s.onload = resolve;
        s.onerror = function () {
          reject(new Error("could not load " + src));
        };
        document.head.appendChild(s);
      });
    }
    return scripts[src];
  }
  function glCapable() {
    if (reduceMotion.matches) return false;
    if (navigator.connection && navigator.connection.saveData) return false;
    if (!window.matchMedia("(min-width: 900px)").matches) return false;
    try {
      var c = document.createElement("canvas");
      var gl = c.getContext("webgl2") || c.getContext("webgl");
      if (!gl) return false;
      var lose = gl.getExtension("WEBGL_lose_context");
      if (lose) lose.loseContext();
      return true;
    } catch (e) {
      return false;
    }
  }
  function bootGL() {
    if (!glCapable()) return;
    var canvas = spec.querySelector("[data-ac-col-canvas]");
    var stage = spec.querySelector("[data-ac-col-stage]");
    if (!canvas || !stage) return;
    var go = function () {
      var own = hero.getAttribute("data-three-src");
      var three = (own ? loadModule(own) : Promise.reject(new Error("no src")))
        .catch(function () {
          return loadModule(THREE_CDN_URL);
        });
      Promise.all([
        three,
        loadScript(hero.getAttribute("data-gl-src")),
        loadScript(hero.getAttribute("data-molecules-src")),
      ])
        .then(function (mods) {
          if (!window.AC_COL_GL || !window.AC_PDP_MOLECULES)
            throw new Error("scene scripts missing");
          scene = window.AC_COL_GL.carousel(mods[0], {
            box: stage,
            canvas: canvas,
            data: window.AC_PDP_MOLECULES,
            keys: thumbs.map(function (t) {
              return t.getAttribute("data-ac-col-spec");
            }),
            index: specIndex,
            onFocus: function (i) {
              showSpecimen(i, true);
            },
            onLive: function () {
              spec.classList.add("is-live");
            },
            onFail: function () {
              scene = null;
              spec.classList.remove("is-live");
            },
          });
        })
        .catch(function (err) {
          scene = null;
          debug("turntable unavailable", err);
        });
    };
    var idle = function () {
      if ("requestIdleCallback" in window)
        requestIdleCallback(go, { timeout: 2500 });
      else setTimeout(go, 400);
    };
    if (document.readyState === "complete") idle();
    else window.addEventListener("load", idle, { once: true });
  }

  /* ------------------------------------------------------------ */
  /* GA4 (window.acGA4 from snippets/ga4-ecommerce.liquid)         */
  /* ------------------------------------------------------------ */

  /* Items come from each card's own data, so view_item_list covers every
     card shown (first paint, filters, sorts, Load more) with its real
     position, and select_item names the clicked product. */
  var listInfo = {
    item_list_name: root.getAttribute("data-list-name") || "",
    item_list_id: root.getAttribute("data-list-id") || "",
  };
  function gaItem(card) {
    return {
      item_id: card.getAttribute("data-item-id"),
      item_name: card.getAttribute("data-item-name"),
      item_brand: "Alliance Chemical",
      item_category: card.getAttribute("data-item-category") || "",
      price: parseFloat(card.getAttribute("data-item-price")) || 0,
      index: parseInt(card.getAttribute("data-index"), 10) || 0,
    };
  }
  function trackList(cards) {
    if (!window.acGA4 || !cards.length) return;
    try {
      window.acGA4.trackEvent(
        "view_item_list",
        Object.assign(
          { items: Array.prototype.map.call(cards, gaItem) },
          listInfo,
        ),
      );
    } catch (e) {
      debug("view_item_list failed", e);
    }
  }
  document.addEventListener(
    "click",
    function (ev) {
      var a = ev.target.closest && ev.target.closest("[data-ac-col-product-link]");
      var card = a && a.closest("[data-ac-col-card]");
      if (!card || !window.acGA4) return;
      var params = Object.assign({ items: [gaItem(card)] }, listInfo);
      var plain =
        ev.button === 0 &&
        !ev.metaKey &&
        !ev.ctrlKey &&
        !ev.shiftKey &&
        !ev.altKey &&
        a.target !== "_blank";
      try {
        if (!plain || typeof window.acGA4.trackNavigationEvent !== "function") {
          window.acGA4.trackEvent("select_item", params);
          return;
        }
        if (a.dataset.acAnalyticsNavigating === "1") return;
        a.dataset.acAnalyticsNavigating = "1";
        ev.preventDefault();
        var destination = a.href;
        window.acGA4.trackNavigationEvent("select_item", params, function () {
          window.location.assign(destination);
        });
      } catch (e) {
        if (a.href) window.location.assign(a.href);
      }
    },
    true,
  );

  /* ------------------------------------------------------------ */
  /* Phones: floating Filters & sort                              */
  /* ------------------------------------------------------------ */

  var fab = root.querySelector("[data-ac-col-fab]");
  var barIO = null;
  function watchToolbar() {
    if (!fab || !("IntersectionObserver" in window)) return;
    if (barIO) barIO.disconnect();
    var bar = root.querySelector("[data-ac-col-toolbar]");
    var sh = shell();
    if (!bar || !sh) return;
    var barGone = false;
    var inResults = false;
    function update() {
      fab.hidden = !(barGone && inResults) || railOpen();
    }
    barIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.target === bar)
          barGone = !en.isIntersecting && en.boundingClientRect.top < 0;
        else inResults = en.isIntersecting;
      });
      update();
    });
    barIO.observe(bar);
    barIO.observe(sh);
  }

  /* ------------------------------------------------------------ */
  /* Back from a product after Load more                           */
  /* ------------------------------------------------------------ */

  /* Load more doesn't change the URL (page 1 stays the canonical list), so
     remember how far the shopper got, per URL, and rebuild that on a
     back/forward visit before restoring their scroll position. */
  function pagesKey() {
    return "ac-col-pages:" + location.pathname + location.search;
  }
  function store(v) {
    try {
      if (v) sessionStorage.setItem(pagesKey(), JSON.stringify(v));
      else sessionStorage.removeItem(pagesKey());
    } catch (e) {
      /* storage blocked: Back shows page one */
    }
  }
  function rememberPages(lastHref) {
    var st = { hrefs: [], y: 0 };
    try {
      st = JSON.parse(sessionStorage.getItem(pagesKey())) || st;
    } catch (e) {
      /* fresh */
    }
    if (st.hrefs.indexOf(lastHref) < 0) st.hrefs.push(lastHref);
    store(st);
  }
  window.addEventListener("pagehide", function () {
    try {
      var st = JSON.parse(sessionStorage.getItem(pagesKey()));
      if (st) {
        st.y = window.scrollY;
        store(st);
      }
    } catch (e) {
      /* nothing to keep */
    }
  });
  function restorePages() {
    var nav =
      performance.getEntriesByType &&
      performance.getEntriesByType("navigation")[0];
    var st = null;
    try {
      st = JSON.parse(sessionStorage.getItem(pagesKey()));
    } catch (e) {
      st = null;
    }
    if (!nav || nav.type !== "back_forward") {
      store(null);
      return;
    }
    if (!st || !st.hrefs || !st.hrefs.length) return;
    if ("scrollRestoration" in history) history.scrollRestoration = "manual";
    var grid = root.querySelector("[data-ac-col-grid]");
    if (!grid) return;
    var chain = Promise.resolve();
    st.hrefs.forEach(function (href) {
      chain = chain.then(function () {
        return fetchSection(href, new AbortController()).then(function (doc) {
          doc
            .querySelectorAll("[data-ac-col-grid] > [data-ac-col-card]")
            .forEach(function (c) {
              grid.appendChild(c);
            });
          var pager = root.querySelector("[data-ac-col-pager]");
          var nextPager = doc.querySelector("[data-ac-col-pager]");
          if (pager && nextPager) {
            var from = pager.querySelector("[data-ac-col-from]");
            var keep = from ? from.textContent : null;
            pager.replaceWith(nextPager);
            var nf = nextPager.querySelector("[data-ac-col-from]");
            if (nf && keep) nf.textContent = keep;
          }
        });
      });
    });
    chain
      .then(function () {
        initCards(grid);
        keepView(grid);
        window.scrollTo(0, st.y || 0);
      })
      .catch(function (err) {
        debug("could not rebuild loaded pages", err);
      });
  }

  /* ------------------------------------------------------------ */
  /* Start                                                         */
  /* ------------------------------------------------------------ */

  initCards(root);
  applyLayout();
  measureToolbar();
  watchToolbar();
  restorePages();
  trackList(root.querySelectorAll("[data-ac-col-card]"));
  if (document.fonts && document.fonts.ready)
    document.fonts.ready.then(function () {
      root.querySelectorAll("[data-ac-col-lineup]").forEach(drawCurve);
      measureToolbar();
    });
})();
